import { Item } from '@zendeskgarden/react-dropdowns'
import { Field, Input, Label } from '@zendeskgarden/react-forms'
import { Body, Close, Footer, FooterItem, Header, Modal } from '@zendeskgarden/react-modals'
import React, { useCallback, useState } from 'react'
import { NavigateFunction } from 'react-router'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { useRerenderOnMutation_deprecated } from '../../hooks/useRerenderOnMutation'
import { RecordFactory } from '../../models/RecordFactory'
import { Source } from '../../models/Source'
import { Subscription } from '../../models/Subscription'
import { isPastDate } from '../../utils'
import {
    POCKETSUITE_USER_ID,
    PREMIUM_PLUS_ANNUAL_ID,
    PREMIUM_PLUS_ID,
    PREMIUM_SUITE_ANNUAL_ID,
    PREMIUM_SUITE_ID,
} from '../../utils/constants'
import { currencyFormat } from '../../utils/numbers'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { Spinner } from '../Spinner'
import { AppHeader } from '../app/AppHeader'
import { PSButton, PSButtonPrimary } from '../app/PSButton'
import { PSDropdown } from '../app/PSDropdown'

type Data = {
    record: Subscription
    sources: Source[]
}

const viewWillAppear = async (base: PocketComponent, navigate?: NavigateFunction) => {
    const subscription = await RecordFactory.getInstance().loadSubscription(base.urlParams.recordID)
    const sources = subscription?.client?.id
        ? await Source.validClientSources(subscription.client.id)
        : []
    if (!subscription) {
        navigate?.('/', { replace: true })
        return
    }
    return {
        record: subscription,
        sources,
    }
}

const render = ({ base, data }: PocketComponentProps<Data>) => {
    const record = useRerenderOnMutation_deprecated(data.record)
    const navigate = usePSNavigate()

    const saveEnabled = !!record.source

    const addCard = useCallback(
        () => navigate(`/client/${record.client?.id}/add-card`),
        [record?.client]
    )

    const description = useCallback(() => {
        let l: string[] = []
        if (record.taxAmount) {
            l.push(`A sales tax of ${currencyFormat(record.taxAmount)} will be included.`)
        }
        if (Number(record.deposit) > 0) {
            l.push(`A setup fee of ${currencyFormat(record.deposit)} will be charged upfront.`)
        }
        return l.join(' ')
    }, [record])

    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [confirmModalLoading, setConfirmModalLoading] = useState(false)

    let pocketsuitePlan: string | undefined = undefined
    if (record.owner?.id === POCKETSUITE_USER_ID && record?.plan) {
        pocketsuitePlan = [PREMIUM_SUITE_ID, PREMIUM_SUITE_ANNUAL_ID].includes(
            record.plan?.id || ''
        )
            ? 'suite'
            : [PREMIUM_PLUS_ID, PREMIUM_PLUS_ANNUAL_ID].includes(record.plan?.id || '')
              ? 'plus'
              : 'premium'
    }
    const sendMessage = useCallback(
        () => navigate(`/messages/${record.client?.id}`),
        [record.client]
    )

    const onError = useCallback((error: any) => {
        setConfirmModalLoading(false)
        base.logAndDisplayError(error)
    }, [])

    const confirmPocketsuiteSubscription = useCallback(() => {
        setConfirmModalLoading(true)
        RecordFactory.getInstance()
            .subscribe(
                record.client!,
                pocketsuitePlan || '',
                record.id!,
                record.source,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined
            )
            .then((result) => (result.ok ? sendMessage() : onError(result.error)))
    }, [pocketsuitePlan, record.client, sendMessage])

    const confirm = () => {
        setConfirmModalLoading(true)
        RecordFactory.getInstance()
            .confirmSubscription(record, true)
            .then((result) => {
                setConfirmModalLoading(false),
                    setShowConfirmModal(false),
                    result.ok ? sendMessage() : onError(result.error)
            })
    }

    return (
        <AppHeader title="Subscription" showBackButton middleWidget={null}>
            <div className="pageWrapper">
                <div className="servicesSettings">
                    <div className="header">
                        <h1>Confirm &amp; Charge</h1>
                        <div style={{ marginLeft: 'auto' }}>
                            <PSButton onClick={addCard} style={{ padding: '21px 16px' }}>
                                Add a Card
                            </PSButton>
                            <PSButtonPrimary
                                onClick={() => setShowConfirmModal(true)}
                                disabled={!saveEnabled}
                                style={{ padding: '21px 16px', marginLeft: '10px' }}
                            >
                                Save
                            </PSButtonPrimary>
                        </div>
                    </div>
                    <div className="separator" />
                    <div className="description">{description()}</div>
                    <div className="form">
                        <Field className="field">
                            <Label className="label">Price</Label>
                            <Input disabled value={currencyFormat(record?.chargeAmount)} />
                        </Field>
                        {record?.allowSurcharge && (
                            <>
                                <Field className="field">
                                    <Label className="label">Surcharge</Label>
                                    <Input
                                        disabled
                                        value={currencyFormat(record?.surchargeAmount)}
                                    />
                                </Field>
                            </>
                        )}

                        <PSDropdown
                            selected={record.source}
                            nameProperty="display_name"
                            downshiftProperty="id"
                            placeholder={<div className="grey">Select a card</div>}
                            label="Payment (required)"
                            onSelect={(selection) => (record.source = selection)}
                        >
                            <>
                                {data.sources.map((source) => (
                                    <Item key={`source-${source.id}`} value={source}>
                                        {source.displayName}
                                    </Item>
                                ))}
                            </>
                        </PSDropdown>
                    </div>
                </div>
                {showConfirmModal && (
                    <Modal onClose={() => setShowConfirmModal(false)}>
                        <Header>Confirm and charge</Header>
                        <Body>
                            {confirmModalLoading ? (
                                <Spinner size={48} />
                            ) : (
                                `Would you like to confirm this subscription${record.date && isPastDate(record.date) ? ' and charge the client' : ''}?`
                            )}
                        </Body>
                        <Close aria-label="Close modal" />
                        <Footer>
                            <FooterItem>
                                {!confirmModalLoading &&
                                    record.owner?.id === POCKETSUITE_USER_ID && (
                                        <PSButtonPrimary onClick={confirmPocketsuiteSubscription}>
                                            {pocketsuitePlan === 'suite'
                                                ? 'Upgrade to Enterprise'
                                                : pocketsuitePlan === 'plus'
                                                  ? 'Upgrade to Team'
                                                  : 'Upgrade to Premium'}
                                        </PSButtonPrimary>
                                    )}
                                {!confirmModalLoading &&
                                    record.owner?.id !== POCKETSUITE_USER_ID && (
                                        <PSButtonPrimary onClick={confirm}>
                                            {record.date && isPastDate(record.date)
                                                ? 'Confirm and charge'
                                                : 'Confirm Subscription'}
                                        </PSButtonPrimary>
                                    )}
                            </FooterItem>
                        </Footer>
                    </Modal>
                )}
            </div>
        </AppHeader>
    )
}

export const NewSubscriptionTotalPage = usePSComponent(render, viewWillAppear)
